import { useCallback, useMemo } from 'react';
import { Edge, ConnectionType } from 'reactflow';
import { ArchitectureEdge, ConnectionType as AppConnectionType } from '../types';
import { CONNECTION_TYPE_CONFIG } from '../constants';
import { MarkerType } from 'reactflow';

// Helper function to get edge style based on connection type and state
export const getEdgeStyle = (
  connectionType: AppConnectionType,
  isHovered: boolean = false,
  isSelected: boolean = false,
  isBidirectional: boolean = false,
  customColor?: string,
  strokeWidth?: 'thin' | 'normal' | 'thick',
  lineStyle?: 'solid' | 'dashed' | 'dotted',
  opacity?: number
) => {
  const config = CONNECTION_TYPE_CONFIG[connectionType];
  const baseColor = customColor || config?.color || '#000000';
  const strokeColor = isSelected ? '#2563eb' : (isHovered ? config?.strokeColor || baseColor : baseColor);
  
  // Map stroke width
  let width: number;
  if (strokeWidth === 'thin') {
    width = isSelected ? 1.5 : (isHovered ? 1.2 : 1);
  } else if (strokeWidth === 'thick') {
    width = isSelected ? 3 : (isHovered ? 2.5 : 2);
  } else {
    width = isSelected ? 2 : (isHovered ? 1.8 : 1.5);
  }
  
      // Map line style - but don't set strokeDasharray if edge is animated (React Flow handles it)
      let strokeDasharray: string | undefined;
      if (lineStyle === 'dashed') {
        strokeDasharray = '8,4';
      } else if (lineStyle === 'dotted') {
        strokeDasharray = '2,4';
      }
      // Note: strokeDasharray will be overridden by React Flow's animation if animated: true
      
      const finalOpacity = opacity !== undefined ? opacity : (isHovered || isSelected ? 1 : 0.8);
      
      return {
        stroke: customColor || strokeColor,
        strokeWidth: width,
        opacity: finalOpacity,
        // Only set strokeDasharray if lineStyle is explicitly set AND edge is not animated
        // (React Flow's animation uses strokeDasharray, so we can't override it)
        ...(strokeDasharray ? { strokeDasharray } : {}),
      };
};

// Helper function to get edge marker style
export const getEdgeMarkerStyle = (connectionType: AppConnectionType, isBidirectional: boolean = false) => {
  const config = CONNECTION_TYPE_CONFIG[connectionType];
  const color = config?.color || '#000000';
  
  return {
    type: MarkerType.ArrowClosed,
    color,
    width: 12,
    height: 12,
  };
};

// Hook for connection styling logic
export const useConnectionStyling = (
  edges: ArchitectureEdge[],
  hoveredEdgeId: string | null,
  selectedEdgeId: string | null
) => {
  const styledEdges = useMemo(() => {
    if (edges.length === 0) return [];
    
    return edges.map(edge => {
      const connectionType = edge.data?.type || AppConnectionType.HTTP;
      const isHovered = hoveredEdgeId === edge.id;
      const isSelected = selectedEdgeId === edge.id;
      const isBidirectional = !!edge.markerStart;
      
      const edgeStyle = getEdgeStyle(
        connectionType, 
        isHovered, 
        isSelected, 
        isBidirectional,
        edge.data?.customColor,
        edge.data?.strokeWidth,
        edge.data?.lineStyle,
        edge.data?.opacity
      );
      const markerStyle = getEdgeMarkerStyle(connectionType, isBidirectional);
      const config = CONNECTION_TYPE_CONFIG[connectionType];
      const finalColor = edge.data?.customColor || config?.color || '#000000';
      
      // Update markerStart for bidirectional connections
      const markerStart = isBidirectional ? {
        ...markerStyle,
        color: finalColor,
      } : undefined;
      
      // Add status indicator styling
      let statusStyle: React.CSSProperties = {};
      if (edge.data?.status === 'inactive') {
        statusStyle.opacity = 0.4;
      } else if (edge.data?.status === 'degraded') {
        // Only set strokeDasharray for degraded if edge is not animated
        // (animated edges need strokeDasharray for the animation effect)
        if (edge.animated === false) {
          statusStyle.strokeDasharray = '4,4';
        }
      }
      
      // Use custom label if provided and visible, otherwise use connection type
      const displayLabel = edge.data?.labelVisible !== false 
        ? (edge.data?.customLabel || edge.data?.type || edge.label || '')
        : '';
      
      // Don't override strokeDasharray if edge is animated (React Flow needs it for animation)
      const isAnimated = edge.animated !== false;
      const finalStyle = {
        ...edge.style,
        ...edgeStyle,
        ...statusStyle,
      };
      
      // Remove strokeDasharray from inline style if animated (let React Flow handle it via CSS)
      // This ensures React Flow's CSS animation can work properly
      if (isAnimated && finalStyle.strokeDasharray) {
        delete finalStyle.strokeDasharray;
      }
      
      return {
        ...edge,
        animated: isAnimated, // Preserve animated property, default to true
        label: displayLabel,
        style: finalStyle,
        labelStyle: {
          ...edge.labelStyle,
          fill: finalColor,
          fontSize: '10px', // Ensure consistent font size
        },
        labelBgStyle: {
          ...edge.labelBgStyle,
          stroke: finalColor,
        },
        markerEnd: {
          ...markerStyle,
          color: finalColor,
        },
        markerStart: markerStart ? {
          ...markerStart,
          color: finalColor,
        } : undefined,
      };
    });
  }, [edges, hoveredEdgeId, selectedEdgeId]);

  return styledEdges;
};
